import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  Paper,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Verified as VerifiedIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { Company } from '../types';
import { CompanyService } from '../services/companyService';
import { useTheme } from '../hooks/useTheme';

const CompaniesPage: React.FC = () => {
  const { currentTheme } = useTheme();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    companySize: '',
    companyType: '',
    isHiring: false,
    isRemoteFriendly: false,
    location: '',
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);

        const companiesData = await CompanyService.getCompanies({
          search: searchQuery || undefined,
          industry: filters.industry || undefined,
          companySize: filters.companySize || undefined,
          companyType: filters.companyType || undefined,
          isHiring: filters.isHiring || undefined,
          isRemoteFriendly: filters.isRemoteFriendly || undefined,
          location: filters.location || undefined,
          limit: 20,
        });

        setCompanies(companiesData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load companies');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [searchQuery, filters]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      industry: '',
      companySize: '',
      companyType: '',
      isHiring: false,
      isRemoteFriendly: false,
      location: '',
    });
    setSearchQuery('');
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== false) || searchQuery;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Companies
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Discover companies and explore career opportunities
              </Typography>
            </Box>
            <Box sx={{ mt: { xs: 2, md: 0 } }}>
              <Button
                component={Link}
                to="/companies/create"
                variant="contained"
                startIcon={<AddIcon />}
                size="large"
                sx={{ borderRadius: 2 }}
              >
                Create Company
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
            {/* Search */}
            <Box>
              <TextField
                fullWidth
                label="Search Companies"
                placeholder="Search by company name, industry, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>

            {/* Industry Filter */}
            <Box>
              <FormControl fullWidth>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={filters.industry}
                  onChange={(e) => handleFilterChange('industry', e.target.value)}
                  label="Industry"
                >
                  <MenuItem value="">All Industries</MenuItem>
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Education">Education</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                  <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="Consulting">Consulting</MenuItem>
                  <MenuItem value="Non-profit">Non-profit</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Company Size Filter */}
            <Box>
              <FormControl fullWidth>
                <InputLabel>Company Size</InputLabel>
                <Select
                  value={filters.companySize}
                  onChange={(e) => handleFilterChange('companySize', e.target.value)}
                  label="Company Size"
                >
                  <MenuItem value="">All Sizes</MenuItem>
                  <MenuItem value="1-10">1-10 employees</MenuItem>
                  <MenuItem value="11-50">11-50 employees</MenuItem>
                  <MenuItem value="51-200">51-200 employees</MenuItem>
                  <MenuItem value="201-500">201-500 employees</MenuItem>
                  <MenuItem value="500+">500+ employees</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Company Type Filter */}
            <Box>
              <FormControl fullWidth>
                <InputLabel>Company Type</InputLabel>
                <Select
                  value={filters.companyType}
                  onChange={(e) => handleFilterChange('companyType', e.target.value)}
                  label="Company Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="nonprofit">Non-profit</MenuItem>
                  <MenuItem value="government">Government</MenuItem>
                  <MenuItem value="startup">Startup</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Second Row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
            {/* Location Filter */}
            <Box>
              <TextField
                fullWidth
                label="Location"
                placeholder="City, State, or Country"
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                InputProps={{
                  startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>

            {/* Checkboxes */}
            <Box>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.isHiring}
                      onChange={(e) => handleFilterChange('isHiring', e.target.checked)}
                    />
                  }
                  label="Currently Hiring"
                />
              </Stack>
            </Box>

            <Box>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.isRemoteFriendly}
                      onChange={(e) => handleFilterChange('isRemoteFriendly', e.target.checked)}
                    />
                  }
                  label="Remote Friendly"
                />
              </Stack>
            </Box>
          </Box>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={clearFilters}
                color="primary"
                size="small"
              >
                Clear all filters
              </Button>
            </Box>
          )}
        </Paper>

        {/* Results */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography color="text.secondary">Loading companies...</Typography>
            </Box>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Error Loading Companies
            </Typography>
            <Typography>{error}</Typography>
          </Alert>
        ) : companies.length === 0 ? (
          <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 2 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No companies found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {hasActiveFilters 
                ? 'Try adjusting your search criteria or clear the filters.'
                : 'No companies have been created yet.'
              }
            </Typography>
            {!hasActiveFilters && (
              <Button
                component={Link}
                to="/companies/create"
                variant="contained"
                startIcon={<AddIcon />}
              >
                Create the first company
              </Button>
            )}
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {companies.map((company) => (
              <Box key={company.id}>
                <Card 
                  component={Link}
                  to={`/companies/${company.slug}`}
                  sx={{ 
                    height: '100%',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      {/* Company Logo */}
                      <Avatar
                        src={company.logoUrl}
                        sx={{ 
                          width: 64, 
                          height: 64,
                          bgcolor: 'primary.main',
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {company.logoUrl ? null : company.name.charAt(0).toUpperCase()}
                      </Avatar>

                      {/* Company Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {company.name}
                          </Typography>
                          {company.isVerified && (
                            <Chip
                              icon={<VerifiedIcon />}
                              label="Verified"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        {company.tagline && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {company.tagline}
                          </Typography>
                        )}

                        <Stack spacing={1} sx={{ mb: 2 }}>
                          {company.industry && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {company.industry}
                              </Typography>
                            </Box>
                          )}
                          {company.companySize && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {company.companySize}
                              </Typography>
                            </Box>
                          )}
                          {company.headquartersCity && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {company.headquartersCity}
                                {company.headquartersCountry && `, ${company.headquartersCountry}`}
                              </Typography>
                            </Box>
                          )}
                        </Stack>

                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {company.isRemoteFriendly && (
                            <Chip
                              icon={<PublicIcon />}
                              label="Remote"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {company.isHiring && (
                            <Chip
                              icon={<WorkIcon />}
                              label="Hiring"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={`${company.profileViews} views`}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* Load More Button */}
        {companies.length > 0 && companies.length >= 20 && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button variant="outlined" size="large">
              Load More Companies
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CompaniesPage;
